import { flip, flow, pipe, tupled, untupled } from "fp-ts/es6/function"
import {
  addIndex,
  always,
  append,
  assoc,
  curry,
  dissoc,
  either,
  evolve,
  find,
  forEachObjIndexed,
  groupBy,
  identity,
  ifElse,
  is,
  juxt,
  map,
  mergeAll,
  nth,
  path,
  prop,
  propSatisfies,
  tail,
} from "ramda"

import * as Logic from "../logic/index.js"

/**
 * Type converter __accepting__ an input of `any` type and __returning__ a `Function`.
 *
 * __If__ the `input` is already a `Function`, it returned as the `output` unchanged.
 *
 * __Otherwise__ the `output` is a `Function` that __returns__ the `input`.
 *
 * @param {function|object} input
 *
 * @returns {function} `output`
 */
export const asFunction = (input) => pipe(input, ifElse(is(Function), identity, always))

export const isContainerized = either(is(Array), is(Object))

export const deepForEachObject = curry((fn, functor) =>
  forEachObjIndexed(ifElse(isContainerized, deepForEachObject(fn), fn), functor),
)

export const deepMap = curry((fn, functor) =>
  map(ifElse(isContainerized, deepMap(fn), fn), functor),
)

export const deepValuesList = (data) => {
  let result = []

  deepForEachObject((value) => (result = append(value, result)), data)
  return result
}

/**
 * Creates a new Object or Record by evolving a shallow copy of the given structure,
 * according to the transformation functions.
 */
export const evolved = flip(evolve)

export const indexedMap = addIndex(map)

export const mappedBy = untupled(
  flow(juxt([flow(nth(0), path), tail]), tupled(groupBy), map(mergeAll)),
)

/**
 * Merges an arbitrary sequence of objects or records together
 * into one Object or Record right to left.
 *
 * @param {...object|Record} args
 * @returns {object|Record} A merged structure.
 */
export const merged = untupled(mergeAll)

/**
 * Creates a reusable mock pattern generator for tests.
 *
 * @param {object|Record} mock
 * Object or Record passing a basic positive scenario test.
 */
export const mock = (mock) =>
  /**
   * @param {object|Record=} parameters
   * @returns {object|Record}
   */
  (parameters) => (parameters ? merged(mock, parameters) : mock)

export const nonEmpty = ({ from, value }) =>
  prop(value, find(propSatisfies(Logic.isNonEmpty, value), from))

/**
 * Renaming the `property` of an `Object` or `Record`
 *
 * @param {string|Symbol} property
 *
 * @param {string|Symbol} name
 *
 * @param {object|Record} data
 *
 * @returns {object|Record}
 */
export const withRenamed = curry((property, name, data) =>
  dissoc(property, assoc(name, data[property], data)),
)
